import { z } from "zod";
import { AiActionPolicyV1Schema } from "./ai-action-policy";
import { ExecutionModeV1Schema } from "./ai-policy";
import { SmartCreationDraftContentV2Schema, SmartCreationDraftSchema } from "./smart-creation";
import { SmartCreationBrowserDraftSchema } from "./smart-creation-browser-draft";
import {
  SmartCreationReviewPlanSettingsSchema,
  SmartCreationReviewWorkspaceSchema,
} from "./smart-creation-review";

export const CampaignCreationTaskStatusSchema = z.enum([
  "queued",
  "running",
  "blocked",
  "needs_attention",
  "failed",
  "completed",
  "simulated",
  "canceled",
]);
export const CampaignCreationTaskPhaseSchema = z.enum([
  "accepted",
  "hierarchy",
  "enrollment",
  "activation",
  "compensation",
  "finalizing",
  "completed",
]);
export const CampaignCreationExecutionModeSchema = z.enum(["amazon", "fake", "local_simulation"]);
export const CampaignCreationOperationKindSchema = z.enum([
  "standalone",
  "sp_hierarchy",
  "sp_additional_group",
  "sp_activation",
  "sp_activation_compensation",
  "delivery_control",
]);
export const CampaignCreationIdentityVerificationSchema = z.enum(["verified", "unverified"]);
const HashSchema = z.string().regex(/^[a-f0-9]{64}$/);
const KeySchema = z.string().min(1).max(256);
const TimestampSchema = z.string().datetime();

export const CampaignCreationTaskIdentitySchema = z
  .object({
    verification: CampaignCreationIdentityVerificationSchema,
    accountId: z.string().uuid().nullable(),
    profileId: z.string().min(1).nullable(),
    marketplaceName: z.string().min(1).nullable(),
    currencyCode: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .nullable(),
  })
  .strict()
  .superRefine((identity, ctx) => {
    if (
      identity.verification === "verified" &&
      [
        identity.accountId,
        identity.profileId,
        identity.marketplaceName,
        identity.currencyCode,
      ].some((value) => value === null)
    )
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Verified creation identity must be complete",
      });
  });
export type CampaignCreationTaskIdentity = z.infer<typeof CampaignCreationTaskIdentitySchema>;

export const CampaignCreationTaskRetryPolicySchema = z
  .object({
    version: z.literal(1),
    maxAttempts: z.number().int().min(1).max(10),
    initialDelayMs: z.number().int().min(2000),
    maxDelayMs: z.number().int().max(60000).min(2000),
    windowMs: z.number().int().positive().max(1800000),
  })
  .strict();
export const DEFAULT_CAMPAIGN_CREATION_TASK_RETRY_POLICY = {
  version: 1,
  maxAttempts: 10,
  initialDelayMs: 2000,
  maxDelayMs: 60000,
  windowMs: 1800000,
} as const;
export const CampaignCreationTaskManifestNodeSchema = z
  .object({
    nodeKey: KeySchema,
    campaignKey: KeySchema,
    parentNodeKey: KeySchema.nullable(),
    operationKind: CampaignCreationOperationKindSchema,
    payload: z.record(z.unknown()),
  })
  .strict();
export const CampaignCreationTaskManifestSchema = z
  .object({
    version: z.literal(1),
    completionContract: z.enum(["create_and_manage", "create_only"]),
    nodes: z.array(CampaignCreationTaskManifestNodeSchema).max(20000),
    retryPolicy: CampaignCreationTaskRetryPolicySchema,
  })
  .strict()
  .superRefine((manifest, ctx) => {
    const nodes = new Map(manifest.nodes.map((node) => [node.nodeKey, node]));
    if (nodes.size !== manifest.nodes.length)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Creation manifest node keys must be unique",
      });
    for (const [index, node] of manifest.nodes.entries()) {
      if (node.parentNodeKey && nodes.get(node.parentNodeKey)?.campaignKey !== node.campaignKey)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Creation parent must exist in the same Campaign",
          path: ["nodes", index, "parentNodeKey"],
        });
      const visited = new Set<string>([node.nodeKey]);
      let parent = node.parentNodeKey;
      while (parent) {
        if (visited.has(parent)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Creation dependencies must be acyclic",
            path: ["nodes", index, "parentNodeKey"],
          });
          break;
        }
        visited.add(parent);
        parent = nodes.get(parent)?.parentNodeKey ?? null;
      }
      if (
        manifest.completionContract === "create_only" &&
        ["sp_activation", "sp_activation_compensation"].includes(node.operationKind)
      )
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Creation-only manifests cannot activate delivery",
          path: ["nodes", index, "operationKind"],
        });
    }
  });
export type CampaignCreationTaskManifest = z.infer<typeof CampaignCreationTaskManifestSchema>;
export type CampaignCreationTaskManifestNode = z.infer<
  typeof CampaignCreationTaskManifestNodeSchema
>;

export const CampaignCreationTaskReviewSchema = z
  .object({
    planSettings: SmartCreationReviewPlanSettingsSchema,
    executionModePolicy: ExecutionModeV1Schema,
    aiActionPolicy: AiActionPolicyV1Schema.optional(),
  })
  .strict();
export const CampaignCreationTaskSnapshotSchema = z
  .object({
    version: z.literal(1),
    draft: SmartCreationDraftSchema,
    preview: SmartCreationDraftContentV2Schema,
    review: CampaignCreationTaskReviewSchema,
    identity: CampaignCreationTaskIdentitySchema,
    capabilityEvidence: z.record(z.unknown()),
    manifest: CampaignCreationTaskManifestSchema,
  })
  .strict();
export type CampaignCreationTaskSnapshot = z.infer<typeof CampaignCreationTaskSnapshotSchema>;

export const CampaignCreationTaskErrorSchema = z
  .object({
    code: z.string().min(1),
    message: z.string().min(1),
    phase: CampaignCreationTaskPhaseSchema,
    recovery: z.enum(["retry", "reconcile", "reauthorize", "edit", "manual_remediation", "none"]),
  })
  .strict();
export type CampaignCreationTaskError = z.infer<typeof CampaignCreationTaskErrorSchema>;
export const CampaignCreationTaskCountsSchema = z
  .object({
    campaigns: z
      .object({
        total: z.number().int().nonnegative(),
        created: z.number().int().nonnegative(),
        active: z.number().int().nonnegative(),
      })
      .strict(),
    adGroups: z
      .object({ total: z.number().int().nonnegative(), created: z.number().int().nonnegative() })
      .strict(),
    targets: z
      .object({ total: z.number().int().nonnegative(), created: z.number().int().nonnegative() })
      .strict(),
  })
  .strict();
export const CampaignCreationTaskItemSchema = z
  .object({
    campaignKey: KeySchema,
    nodeKey: KeySchema,
    campaignName: z.string(),
    status: z.enum(["pending", "processing", "succeeded", "partial", "failed", "unknown"]),
    currentStep: z.string().nullable(),
    amazonCampaignId: z.string().nullable(),
    localCampaignId: z.string().uuid().nullable(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
  })
  .strict();
export type CampaignCreationTaskItem = z.infer<typeof CampaignCreationTaskItemSchema>;
export const CampaignCreationTaskSchema = z
  .object({
    id: z.string().uuid(),
    shopId: z.string().uuid(),
    createdByUserId: z.string().uuid().nullable(),
    status: CampaignCreationTaskStatusSchema,
    phase: CampaignCreationTaskPhaseSchema,
    executionMode: CampaignCreationExecutionModeSchema,
    identityVerification: CampaignCreationIdentityVerificationSchema,
    revision: z.number().int().positive(),
    dispatchGeneration: z.number().int().nonnegative(),
    activationRound: z.number().int().nonnegative(),
    managedGroupId: z.string().uuid().nullable(),
    managedGroupVersion: z.number().int().positive().nullable(),
    lastError: CampaignCreationTaskErrorSchema.nullable(),
    counts: CampaignCreationTaskCountsSchema,
    items: z.array(CampaignCreationTaskItemSchema),
    allowedActions: z.array(z.enum(["retry", "reconcile"])),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
    finishedAt: TimestampSchema.nullable(),
  })
  .strict();
export type CampaignCreationTask = z.infer<typeof CampaignCreationTaskSchema>;
export const CampaignCreationTaskResponseSchema = z
  .object({ success: z.literal(true), data: CampaignCreationTaskSchema })
  .strict();
export const CampaignCreationTaskRetryInputSchema = z
  .object({ expectedRevision: z.number().int().positive() })
  .strict();
export const CampaignCreationTaskReconcileInputSchema = CampaignCreationTaskRetryInputSchema.extend(
  { nodeKey: KeySchema },
);
export const CampaignCreationTaskListInputSchema = z
  .object({
    status: CampaignCreationTaskStatusSchema.optional(),
    recent: z
      .preprocess(
        (value) => (value === "true" ? true : value === "false" ? false : value),
        z.boolean(),
      )
      .optional(),
    cursor: z.string().uuid().optional(),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
  })
  .strict();
export const CampaignCreationTaskListResponseSchema = z
  .object({
    success: z.literal(true),
    data: z.array(CampaignCreationTaskSchema),
    pagination: z
      .object({
        page: z.number().int().positive(),
        pageSize: z.number().int().positive(),
        total: z.number().int().nonnegative(),
        hasMore: z.boolean(),
      })
      .strict(),
  })
  .strict();

export const CampaignCreationTaskSubmitReviewInputSchema = z
  .object({
    expectedPlanRevision: z.number().int().positive(),
    expectedWorkspaceRevision: z.number().int().positive(),
    contentHash: HashSchema,
    editedReview: SmartCreationReviewWorkspaceSchema.optional(),
  })
  .strict();

export const CampaignCreationTaskSubmissionInputSchema = z
  .object({
    submissionId: z.string().uuid(),
    plan: SmartCreationBrowserDraftSchema,
    signature: HashSchema,
    input: CampaignCreationTaskSubmitReviewInputSchema,
  })
  .strict();
export type CampaignCreationTaskSubmissionInput = z.infer<
  typeof CampaignCreationTaskSubmissionInputSchema
>;

export type CampaignCreationTaskSubmitReviewInput = z.infer<
  typeof CampaignCreationTaskSubmitReviewInputSchema
>;
